import { useEffect, useState } from 'react';

/**
 * Top banner that appears whenever the browser reports it is offline.
 *
 * The banner is purely informational: TanStack Query already pauses fetches
 * via its built-in `onlineManager`, and `apiFetch` short-circuits mutations
 * with an `ApiError(network_down)`. We deliberately do not block the UI,
 * because users may still be reading cached data or filling out a form
 * whose draft state we want to preserve until they regain connectivity
 * (Requirement 20.4 + 25.2).
 *
 * Validates Requirement 25.2.
 */
export default function NetworkBanner(): JSX.Element | null {
  const [online, setOnline] = useState<boolean>(() =>
    // SSR / unit-test safety: default to online if `navigator` is unavailable.
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const onOnline = (): void => setOnline(true);
    const onOffline = (): void => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (online) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-40 bg-amber-500 px-4 py-2 text-center text-sm font-medium text-amber-950 shadow-md"
    >
      Mất kết nối tới máy chủ
    </div>
  );
}
